import { z } from 'zod';

/*
 * Feedback: a quick reaction and/or a short note to the Nirvik team (the
 * default) or to one of the user's organizations. See the API's
 * services/feedback.ts.
 */

export const FEEDBACK_REACTIONS = [1, 2, 3, 4, 5] as const;
export const REACTION_FACES: Record<number, { face: string; label: string }> = {
  1: { face: '😞', label: 'Very unhappy' },
  2: { face: '🙁', label: 'Unhappy' },
  3: { face: '😐', label: 'Neutral' },
  4: { face: '🙂', label: 'Happy' },
  5: { face: '😍', label: 'Love it' },
};
export const FEEDBACK_CATEGORIES = ['idea', 'problem', 'praise', 'other'] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];
export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  idea: 'Idea',
  problem: 'Problem',
  praise: 'Praise',
  other: 'Other',
};
export const FEEDBACK_MAX_LENGTH = 1000;
export const FEEDBACK_PER_DAY = 10;
export const FEEDBACK_STATUSES = ['new', 'read', 'archived'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const feedbackInputSchema = z
  .object({
    /** One of the user's orgs; null or missing for the Nirvik team. */
    orgId: z.string().uuid().nullable().optional(),
    reaction: z.number().int().min(1).max(5).nullable().optional(),
    category: z.enum(FEEDBACK_CATEGORIES).nullable().optional(),
    message: z.string().trim().max(FEEDBACK_MAX_LENGTH).nullable().optional(),
    /** Don't share my name with the org (platform admins still see it). */
    anonymous: z.boolean().default(false),
    /** The page it was sent from (a path, e.g. /c/spot-the-scam). */
    pageUrl: z
      .string()
      .max(500)
      .regex(/^\/[^\s]*$/, 'A path on this site')
      .nullable()
      .optional(),
  })
  .refine((f) => f.reaction || f.message, { message: 'Pick a reaction or write a note' });
export type FeedbackInput = z.infer<typeof feedbackInputSchema>;

export const feedbackQuerySchema = z.object({
  /** `inbox` (new and read), `unread`, or `archived`. */
  view: z.enum(['inbox', 'unread', 'archived']).default('inbox'),
  reaction: z.coerce.number().int().min(1).max(5).optional(),
  category: z.enum(FEEDBACK_CATEGORIES).optional(),
  q: z.string().trim().max(100).optional(),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type FeedbackQuery = z.infer<typeof feedbackQuerySchema>;

export const feedbackStatusSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
  status: z.enum(FEEDBACK_STATUSES),
});
